// Onboarding wizard — original 5-step "create family" path plus a
// new "join existing family" branch. See docs/ONBOARDING.md §Step 1.
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ApiError, api } from "../lib/api.js";
import { useFamily, setActiveFamilyId } from "../lib/family.js";
import { useAuth } from "../lib/auth.js";
import { isValidPhone as validPhone, normalizePhone } from "../lib/phone.js";

// Top-level step is "choose" first; from there the user branches into either
// the existing create-family flow or the new join-existing-family flow.
type Step =
  | "choose"
  | "name"
  | "location"
  | "phone"
  | "provisioning"
  | "ready"
  | "join_admin"
  | "join_self"
  | "join_submitted";

export function Onboarding() {
  const navigate = useNavigate();
  const { session } = useAuth();
  const { refresh } = useFamily();
  const [step, setStep] = useState<Step>("choose");
  const [error, setError] = useState<string | null>(null);
  const [familyName, setFamilyName] = useState(suggestFamilyName(session?.email ?? ""));
  const [homeLocation, setHomeLocation] = useState("");
  const [adminDisplayName, setAdminDisplayName] = useState("");
  const [adminPhone, setAdminPhone] = useState("");
  const [adminSmsConsent, setAdminSmsConsent] = useState(false);
  const [provisionedNumber, setProvisionedNumber] = useState<string | null>(null);
  // Join-existing-family flow state
  const [joinAdminEmail, setJoinAdminEmail] = useState("");
  const [joinAdminPhone, setJoinAdminPhone] = useState("");
  const [joinRequesterName, setJoinRequesterName] = useState("");
  const [joinRequesterPhone, setJoinRequesterPhone] = useState("");
  const [joinMessage, setJoinMessage] = useState("");
  const [joinDeliveredVia, setJoinDeliveredVia] = useState<"email" | "sms" | null>(null);

  async function onSubmitJoin() {
    setError(null);
    if (!joinRequesterName.trim()) {
      setError("Please add your name.");
      return;
    }
    if (!joinAdminEmail.trim() && !joinAdminPhone.trim()) {
      setError("Please tell us either the family admin's email or phone.");
      return;
    }
    if (!session?.email && !joinRequesterPhone.trim()) {
      setError("Please add your phone so the admin can reach you.");
      return;
    }
    try {
      const res = await api.submitJoinRequest({
        adminEmail: joinAdminEmail.trim() || null,
        adminPhone: joinAdminPhone.trim() ? normalizePhone(joinAdminPhone) : null,
        requesterName: joinRequesterName.trim(),
        requesterEmail: session?.email ?? null,
        requesterPhone: joinRequesterPhone.trim() ? normalizePhone(joinRequesterPhone) : null,
        message: joinMessage.trim() || null,
      });
      setJoinDeliveredVia(res.deliveredVia ?? null);
      setStep("join_submitted");
    } catch (err) {
      const raw = err instanceof Error ? err.message : "Couldn't send the request.";
      setError(
        /admin_not_found|404/i.test(raw)
          ? "We couldn't find a family with that admin contact. Double-check the email or phone."
          : raw,
      );
    }
  }

  async function onProvision() {
    setError(null);
    setStep("provisioning");
    try {
      const { family, admin } = await api.createFamily({
        name: familyName.trim(),
        homeLocation: homeLocation.trim() || null,
        homeTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        adminDisplayName: adminDisplayName.trim(),
        adminPhoneNumber: normalizePhone(adminPhone),
      });
      setProvisionedNumber(family.twilioPhoneNumber);
      setActiveFamilyId(family.id);
      await refresh();
      void admin;
      setStep("ready");
    } catch (err) {
      const errBody = err instanceof ApiError ? err.body : null;
      if (err instanceof ApiError && (errBody as any)?.error === "already_has_family") {
        const existingFamilyName =
          (errBody as { existingFamilyName?: string }).existingFamilyName ?? "your family";
        setError(
          `Looks like this phone number already has a family called "${existingFamilyName}". Try signing in again from the home page instead of starting a new one — it should take you straight there.`,
        );
        setStep("phone");
        return;
      }

      const raw = err instanceof Error ? err.message : "";
      // Tightened from a broad substring match: only swallow the raw error when
      // it's clearly a provision-side / upstream failure. Bare "number" matched
      // unrelated validation errors (card number, missing number field) and
      // "twilio" leaked vendor names into user-facing copy.
      const isProvisionFailure =
        /\bprovision(ing)?\s+(failed|error)\b/i.test(raw) ||
        /\b(502|503|504)\b/.test(raw);
      const friendly = isProvisionFailure
        ? "We couldn't reserve a phone number for your family right now. Tap 'Get my family number' to try again — this usually clears within a minute."
        : raw || "Couldn't provision a number.";
      setError(friendly);
      setStep("phone"); // back to phone step so they can retry
    }
  }

  return (
    <main className="min-h-screen flex items-center justify-center px-6 py-10">
      <div className="max-w-md w-full">
        <ProgressDots step={step} />
        {step === "choose" && (
          <Card
            title="Welcome to Inkling"
            subtitle="Are you starting a new family calendar, or joining one that someone else has already set up? (Family means whatever it means to you — partners, kids, co-parents, roommates, chosen family.)"
            illustration={
              <picture>
                <source srcSet="/inkling-onboarding-welcome.webp" type="image/webp" />
                <img
                  src="/inkling-onboarding-welcome.jpg"
                  alt="A hand gently setting down a phone on a wooden table — a soft pastel light blooming behind the screen, a sage sprig and pencil resting nearby."
                  className="w-full h-auto rounded-xl"
                  loading="eager"
                  decoding="async"
                />
              </picture>
            }
          >
            <button
              onClick={() => setStep("name")}
              className="btn-primary w-full"
            >
              Start a new family
            </button>
            <button
              onClick={() => setStep("join_admin")}
              className="btn-secondary w-full"
            >
              Join an existing family
            </button>
          </Card>
        )}
        {step === "join_admin" && (
          <Card
            title="Join an existing family"
            subtitle="Tell us how to reach the admin who already set up your family on Inkling. We'll send them a request to add you."
            illustration={
              <picture>
                <source srcSet="/inkling-onboarding-join.webp" type="image/webp" />
                <img
                  src="/inkling-onboarding-join.jpg"
                  alt="Two hands meeting from opposite sides of a watercolor wash — one holding a phone with a soft glowing speech bubble, the other reaching open-palm to receive it."
                  className="w-full h-auto rounded-xl"
                  loading="lazy"
                  decoding="async"
                />
              </picture>
            }
          >
            <input
              autoFocus
              type="email"
              value={joinAdminEmail}
              onChange={(e) => setJoinAdminEmail(e.target.value)}
              placeholder="Admin's email (e.g. casey@example.com)"
              className="input"
            />
            <p className="text-xs text-stone-500 -mt-1">…or…</p>
            <input
              type="tel"
              value={joinAdminPhone}
              onChange={(e) => setJoinAdminPhone(e.target.value)}
              placeholder="Admin's phone (e.g. +1 555 555 1234)"
              className="input"
            />
            <div className="flex gap-2">
              <button onClick={() => setStep("choose")} className="btn-secondary">Back</button>
              <button
                onClick={() => setStep("join_self")}
                disabled={!joinAdminEmail.trim() && !joinAdminPhone.trim()}
                className="btn-primary flex-1"
              >
                Next
              </button>
            </div>
          </Card>
        )}
        {step === "join_self" && (
          <Card
            title="Your details"
            subtitle="So the admin knows who's asking and can reach you when they approve."
          >
            <input
              autoFocus
              type="text"
              value={joinRequesterName}
              onChange={(e) => setJoinRequesterName(e.target.value)}
              placeholder="Your name (e.g. Joshua)"
              required
              className="input"
            />
            <input
              type="tel"
              value={joinRequesterPhone}
              onChange={(e) => setJoinRequesterPhone(e.target.value)}
              placeholder="Your phone (so you can text events in once approved)"
              className="input"
            />
            <textarea
              value={joinMessage}
              onChange={(e) => setJoinMessage(e.target.value.slice(0, 500))}
              placeholder="Optional note to the admin (e.g. 'Hi, I'm Joshua's dad')"
              rows={3}
              className="input"
            />
            {error && <p className="text-sm text-red-600">{error}</p>}
            <div className="flex gap-2">
              <button onClick={() => setStep("join_admin")} className="btn-secondary">Back</button>
              <button onClick={onSubmitJoin} className="btn-primary flex-1">
                Send request
              </button>
            </div>
          </Card>
        )}
        {step === "join_submitted" && (
          <Card
            title="Request sent"
            subtitle={
              joinDeliveredVia === "sms"
                ? "We texted the admin. Once they approve, you'll be able to text events to the family number."
                : "We emailed the admin. Once they approve, you'll be able to text events to the family number."
            }
          >
            <p className="text-sm text-stone-600">
              You can close this tab. We'll let you know once the admin approves your request — check your email
              {joinRequesterPhone ? " or texts" : ""} in the meantime.
            </p>
            <a href="/" className="btn-secondary inline-block">Back to start</a>
          </Card>
        )}
        {step === "name" && (
          <Card title="What's your family called?" subtitle="This shows up on confirmation messages and in your shared calendar.">
            <input
              autoFocus
              type="text"
              value={familyName}
              onChange={(e) => setFamilyName(e.target.value)}
              placeholder="e.g. Smith"
              className="input"
            />
            <button
              disabled={!familyName.trim()}
              onClick={() => setStep("location")}
              className="btn-primary"
            >
              Next
            </button>
          </Card>
        )}

        {step === "location" && (
          <Card title="Where's home?" subtitle="So we can resolve 'school' or 'soccer practice' to the right city. You can leave this blank.">
            <input
              autoFocus
              type="text"
              value={homeLocation}
              onChange={(e) => setHomeLocation(e.target.value)}
              placeholder="e.g. your home city"
              className="input"
            />
            <div className="flex gap-2">
              <button onClick={() => setStep("name")} className="btn-secondary">Back</button>
              <button onClick={() => setStep("phone")} className="btn-primary flex-1">
                Next
              </button>
            </div>
          </Card>
        )}

        {step === "phone" && (
          <Card title="Your name and phone" subtitle="So we know who's texting events in. Use the phone you'll text from.">
            <input
              autoFocus
              type="text"
              value={adminDisplayName}
              onChange={(e) => setAdminDisplayName(e.target.value)}
              placeholder="Your name"
              className="input mb-3"
            />
            <input
              type="tel"
              value={adminPhone}
              onChange={(e) => setAdminPhone(e.target.value)}
              placeholder="+1 555 555 1234"
              className="input"
            />
            <label className="mt-4 flex items-start gap-3 text-sm text-stone-700">
              <input
                type="checkbox"
                checked={adminSmsConsent}
                onChange={(e) => setAdminSmsConsent(e.target.checked)}
                className="mt-1 size-4 rounded border-stone-400 text-stone-900 focus:ring-stone-900"
              />
              <span>
                I agree to receive SMS messages from <strong>Inkling</strong> at this number —
                invitations, confirmations, and clarification messages about events I text in.
                Reply <strong>STOP</strong> to opt out at any time. Msg &amp; data rates may apply.
                See our{" "}
                <a href="/sms-consent" target="_blank" rel="noopener noreferrer" className="underline">
                  SMS terms
                </a>.
              </span>
            </label>
            {error && <p className="text-sm text-red-600 mt-2">{error}</p>}
            <div className="flex gap-2 mt-4">
              <button onClick={() => setStep("location")} className="btn-secondary">Back</button>
              <button
                disabled={!adminDisplayName.trim() || !validPhone(adminPhone) || !adminSmsConsent}
                onClick={onProvision}
                className="btn-primary flex-1"
              >
                Get my family number
              </button>
            </div>
          </Card>
        )}

        {step === "provisioning" && (
          <Card title="Getting your family's number…" subtitle="One moment — we're picking a phone number and wiring it up.">
            <div className="flex justify-center py-8">
              <div className="size-8 rounded-full border-4 border-stone-300 border-t-stone-900 animate-spin" />
            </div>
          </Card>
        )}

        {step === "ready" && provisionedNumber && (
          <Card
            title="Here's your family's number"
            subtitle="Save it as 'Inkling' in your contacts. Text it your first event right now to see it appear on your calendar."
            illustration={
              <picture>
                <source srcSet="/inkling-onboarding-ready.webp" type="image/webp" />
                <img
                  src="/inkling-onboarding-ready.jpg"
                  alt="Two cupped hands gently cradling a phone with a soft golden halo and pastel petals drifting around — the moment your family number arrives."
                  className="w-full h-auto"
                  loading="eager"
                  decoding="async"
                />
              </picture>
            }
          >
            <div className="rounded-lg bg-stone-100 p-6 text-center">
              <p className="text-3xl font-mono font-medium tracking-tight">
                {prettyPhone(provisionedNumber)}
              </p>
            </div>
            <p className="text-sm text-stone-600 mt-4">
              Try texting it: <em>"Soccer practice tomorrow at 4pm at the park"</em>
            </p>
            <button
              onClick={() => navigate("/calendar")}
              className="btn-primary"
            >
              Go to my calendar
            </button>
          </Card>
        )}
      </div>
    </main>
  );
}

function ProgressDots({ step }: { step: Step }) {
  const order: Step[] = ["name", "location", "phone", "provisioning", "ready"];
  const active = order.indexOf(step);
  return (
    <div className="flex justify-center gap-2 mb-8">
      {order.map((s, i) => (
        <span
          key={s}
          className={`size-2 rounded-full ${i <= active ? "bg-stone-900" : "bg-stone-300"}`}
        />
      ))}
    </div>
  );
}

function Card({
  title,
  subtitle,
  illustration,
  children,
}: {
  title: string;
  subtitle?: string;
  illustration?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-2xl border border-stone-200 bg-white p-6 shadow-sm">
      {illustration && (
        <div className="-mx-6 -mt-6 mb-6 overflow-hidden rounded-t-2xl bg-stone-50">
          {illustration}
        </div>
      )}
      <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
      {subtitle && <p className="mt-2 text-stone-600">{subtitle}</p>}
      <div className="mt-6 space-y-3">{children}</div>
    </div>
  );
}

function suggestFamilyName(email: string): string {
  const local = email.split("@")[0] ?? "";
  return local ? `${local.charAt(0).toUpperCase() + local.slice(1)}` : "";
}

// normalizePhone / validPhone re-exported from lib/phone.ts so the same
// libphonenumber-js parsing rules apply across Onboarding + Members.
function prettyPhone(p: string): string {
  const m = p.match(/^\+1(\d{3})(\d{3})(\d{4})$/);
  if (m) return `(${m[1]}) ${m[2]}-${m[3]}`;
  return p;
}
